import { useEffect, useMemo, useState, useCallback } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, Check, Clock, Calendar as CalIcon, User, Scissors, Store, CreditCard, MapPin } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Calendar } from "@/components/ui/calendar";
import ShareButtons from "@/components/ShareButtons";
import { CANCELLATION_WINDOW_HOURS } from "@/lib/policies";

const STEPS = ["Location", "Service", "Date & Time", "Your Details", "Confirmed"];

function fmtDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export default function Booking() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [step, setStep] = useState(0);
  const [services, setServices] = useState([]);
  const [locations, setLocations] = useState([]);
  const [location, setLocation] = useState(null); // "wilson" | "clayton"
  const [selected, setSelected] = useState(null);
  const [date, setDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slotsError, setSlotsError] = useState(false);
  const [timeSlot, setTimeSlot] = useState(null);
  const [details, setDetails] = useState({ customer_name: "", customer_phone: "", customer_email: "", notes: "" });
  const [payChoice, setPayChoice] = useState("shop");
  const [payEnabled, setPayEnabled] = useState(false);
  const [giftCode, setGiftCode] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  useEffect(() => {
    api.get("/payments/config").then((r) => setPayEnabled(!!r.data.enabled)).catch(() => {});
    api.get("/locations").then((r) => setLocations(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/services").then((r) => {
      setServices(r.data);
      const pre = params.get("service");
      if (pre) {
        const found = r.data.find((s) => s.id === pre);
        // Don't auto-skip location — force explicit pick. Just stash the service.
        if (found) setSelected(found);
      }
    });
    const preDate = params.get("date");
    if (preDate && /^\d{4}-\d{2}-\d{2}$/.test(preDate)) {
      setDate(new Date(preDate + "T00:00:00"));
    }
  }, [params]);

  useEffect(() => {
    if (user) {
      setDetails((d) => ({
        ...d,
        customer_name: d.customer_name || user.name || "",
        customer_email: d.customer_email || user.email || "",
        customer_phone: d.customer_phone || user.phone || "",
      }));
    }
  }, [user]);

  const loadSlots = useCallback(() => {
    if (!date || !location) return;
    setSlotsLoading(true);
    setSlotsError(false);
    setTimeSlot(null);
    api.get(`/availability?date=${fmtDate(date)}&location=${location}`)
      .then((r) => setSlots(r.data.slots))
      .catch(() => setSlotsError(true))
      .finally(() => setSlotsLoading(false));
  }, [date, location]);

  useEffect(() => { loadSlots(); }, [loadSlots]);

  const grouped = useMemo(() => {
    const g = {};
    services.forEach((s) => { (g[s.category] = g[s.category] || []).push(s); });
    return g;
  }, [services]);

  const submit = async () => {
    if (!details.customer_name || !details.customer_phone) {
      toast.error("Please provide your name and phone number.");
      return;
    }
    setSubmitting(true);
    try {
      const useOnline = payEnabled && payChoice === "online" && !giftCode.trim();
      const payload = {
        service_id: selected.id,
        location,
        date: fmtDate(date),
        time_slot: timeSlot,
        customer_name: details.customer_name,
        customer_phone: details.customer_phone,
        customer_email: details.customer_email || null,
        notes: details.notes,
        payment_choice: useOnline ? "online" : "shop",
        gift_card_code: giftCode.trim() || null,
      };
      const { data } = await api.post("/appointments", payload);

      if (useOnline) {
        // Persist a lightweight ref so /payment/success can show details
        sessionStorage.setItem("tbc_pending_appt", JSON.stringify(data));
        const { data: co } = await api.post("/payments/checkout", {
          appointment_id: data.id,
          origin_url: window.location.origin,
        });
        window.location.href = co.checkout_url;
        return;
      }

      setConfirmation(data);
      setStep(4);
      toast.success("Appointment confirmed!");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Booking failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] pt-32 pb-24 px-6 lg:px-12" data-testid="booking-page">
      <div className="max-w-4xl mx-auto">
        {/* Progress */}
        <div className="flex items-center justify-between mb-16">
          {STEPS.map((label, i) => (
            <div key={label} className="flex items-center flex-1 last:flex-none">
              <div className="flex flex-col items-center gap-2">
                <div className={`w-9 h-9 flex items-center justify-center border overline text-xs transition-colors duration-300 ${
                  i < step ? "bg-[#d4af37] border-[#d4af37] text-black" : i === step ? "border-[#d4af37] text-[#d4af37]" : "border-white/20 text-white/30"
                }`}>
                  {i < step ? <Check className="w-4 h-4" /> : i + 1}
                </div>
                <span className={`overline text-[0.55rem] hidden sm:block ${i <= step ? "text-white/70" : "text-white/30"}`}>{label}</span>
              </div>
              {i < STEPS.length - 1 && <div className={`h-px flex-1 mx-3 ${i < step ? "bg-[#d4af37]" : "bg-white/15"}`} />}
            </div>
          ))}
        </div>

        <AnimatePresence mode="wait">
          {/* STEP 0: Location */}
          {step === 0 && (
            <motion.div key="s0" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.4 }} data-testid="step-location">
              <h1 className="font-display text-3xl lg:text-5xl text-white mb-2">Where would you like your cut?</h1>
              <p className="text-white/50 font-body mb-10">Reek serves clients at two spots. Choose the one that works for you.</p>

              <div className="grid md:grid-cols-2 gap-4">
                {locations.map((loc) => {
                  const active = location === loc.id;
                  return (
                    <button
                      key={loc.id}
                      onClick={() => setLocation(loc.id)}
                      className={`text-left border p-6 transition-colors duration-300 ${active ? "border-[#d4af37] bg-[#d4af37]/[0.06]" : "border-white/10 bg-[#0a0a0a] hover:border-[#d4af37]/40"}`}
                      data-testid={`choose-location-${loc.id}`}
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-2 overline text-xs text-[#d4af37]">
                          <MapPin className="w-4 h-4" strokeWidth={1.5} /> {loc.short}
                        </div>
                        {active && <Check className="w-4 h-4 text-[#d4af37]" />}
                      </div>
                      <p className="font-display text-xl text-white leading-snug mb-1">{loc.name}</p>
                      <p className="font-body text-sm text-white/60 mb-3">{loc.address_line}, {loc.city}, {loc.state} {loc.zip}</p>
                      <p className="flex items-center gap-2 overline text-[0.6rem] text-[#d4af37]/80 tracking-widest mb-3">
                        <Clock className="w-3.5 h-3.5" strokeWidth={1.5} /> {loc.hours_label}
                      </p>
                      <p className="text-white/40 font-body text-xs italic">{loc.note}</p>
                    </button>
                  );
                })}
              </div>

              <div className="flex justify-end mt-12">
                <button
                  onClick={() => setStep(1)}
                  disabled={!location}
                  className="group inline-flex items-center gap-2 bg-[#d4af37] text-black px-8 py-3.5 overline text-xs disabled:opacity-30 disabled:cursor-not-allowed hover:bg-[#c5a028] transition-colors"
                  data-testid="continue-to-service"
                >
                  Continue <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            </motion.div>
          )}

          {/* STEP 1: Service */}
          {step === 1 && (
            <motion.div key="s1" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.4 }} data-testid="step-service">
              <h1 className="font-display text-3xl lg:text-5xl text-white mb-2">Choose your service</h1>
              <p className="text-white/50 font-body mb-2">Select what you&apos;re coming in for.</p>
              {location && (
                <p className="overline text-[0.65rem] text-[#d4af37] mb-6 tracking-widest flex items-center gap-2" data-testid="picked-location">
                  <MapPin className="w-3.5 h-3.5" /> {locations.find((l) => l.id === location)?.short}
                </p>
              )}
              <div className="border-l-2 border-[#d4af37] pl-5 py-1 mb-10" data-testid="premium-message">
                <p className="text-white/70 font-body text-sm italic leading-relaxed max-w-2xl">
                  Every appointment at The Barbers Chair is designed to deliver more than a haircut — it&apos;s a premium grooming experience focused on precision, professionalism and consistency. Book with confidence knowing you&apos;ll leave looking your best.
                </p>
              </div>
              {Object.entries(grouped).map(([cat, items]) => (
                <div key={cat} className="mb-10">
                  <p className="overline text-xs text-[#d4af37] mb-4">{cat}</p>
                  <div className="divide-y divide-white/10 border-y border-white/10">
                    {items.map((s) => (
                      <button
                        key={s.id}
                        onClick={() => { setSelected(s); setStep(2); }}
                        className="group w-full flex items-center justify-between gap-4 py-5 text-left hover:px-2 transition-[padding] duration-300"
                        data-testid={`choose-service-${s.id}`}
                      >
                        <div>
                          <p className="font-body text-white text-lg group-hover:text-[#d4af37] transition-colors">{s.name}</p>
                          <p className="overline text-[0.55rem] text-white/40 mt-1">{s.duration} min</p>
                        </div>
                        <div className="flex items-center gap-6">
                          <span className="font-display text-xl text-white">${s.price}</span>
                          <ArrowRight className="w-4 h-4 text-white/30 group-hover:text-[#d4af37] group-hover:translate-x-1 transition-[transform,color] duration-300" />
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
              <div className="flex justify-start mt-8">
                <button onClick={() => setStep(0)} className="inline-flex items-center gap-2 overline text-xs text-white/50 hover:text-white transition-colors" data-testid="back-to-location">
                  <ArrowLeft className="w-4 h-4" /> Back
                </button>
              </div>
            </motion.div>
          )}

          {/* STEP 2: Date & Time */}
          {step === 2 && (
            <motion.div key="s2" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.4 }} data-testid="step-datetime">
              <h1 className="font-display text-3xl lg:text-5xl text-white mb-2">Pick date & time</h1>
              <p className="text-white/50 font-body mb-2 flex items-center gap-2"><Scissors className="w-4 h-4 text-[#d4af37]" /> {selected?.name} · ${selected?.price}</p>
              {location && (
                <p className="overline text-[0.65rem] text-[#d4af37] mb-10 tracking-widest flex items-center gap-2">
                  <MapPin className="w-3.5 h-3.5" /> {locations.find((l) => l.id === location)?.short} · {locations.find((l) => l.id === location)?.hours_label}
                </p>
              )}

              <div className="grid md:grid-cols-2 gap-10">
                <div className="border border-white/10 p-4 bg-[#0a0a0a] flex justify-center">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={setDate}
                    disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                    data-testid="booking-calendar"
                  />
                </div>

                <div>
                  <p className="overline text-xs text-[#d4af37] mb-4 flex items-center gap-2"><Clock className="w-4 h-4" /> Available Times</p>
                  {!date && <p className="text-white/40 font-body text-sm">Select a date to see open slots.</p>}
                  {date && slotsLoading && <p className="text-white/40 font-body text-sm animate-pulse">Loading slots…</p>}
                  {date && !slotsLoading && slotsError && (
                    <div className="border border-red-400/20 bg-red-400/5 p-4" data-testid="slots-error">
                      <p className="text-red-400 font-body text-sm mb-3">Couldn&apos;t load open slots. Check your connection and try again.</p>
                      <button onClick={loadSlots} className="overline text-[0.65rem] border border-red-400/40 text-red-300 px-4 py-2 hover:bg-red-400/10 transition-colors" data-testid="retry-slots-btn">Retry</button>
                    </div>
                  )}
                  {date && !slotsLoading && !slotsError && slots.length === 0 && (
                    <p className="text-white/40 font-body text-sm">No open slots on this day. Try another date.</p>
                  )}
                  {date && !slotsLoading && !slotsError && slots.length > 0 && (
                    <div className="grid grid-cols-3 gap-2 max-h-[320px] overflow-y-auto pr-1">
                      {slots.map((s) => (
                        <button
                          key={s.time}
                          disabled={!s.available}
                          onClick={() => setTimeSlot(s.time)}
                          className={`py-3 overline text-[0.65rem] border transition-colors duration-200 ${
                            timeSlot === s.time
                              ? "bg-[#d4af37] border-[#d4af37] text-black"
                              : s.available
                              ? "border-white/15 text-white/70 hover:border-[#d4af37] hover:text-[#d4af37]"
                              : "border-white/5 text-white/20 line-through cursor-not-allowed"
                          }`}
                          data-testid={`slot-${s.time}`}
                        >
                          {s.time}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-between mt-12">
                <button onClick={() => setStep(1)} className="inline-flex items-center gap-2 overline text-xs text-white/50 hover:text-white transition-colors" data-testid="back-to-service">
                  <ArrowLeft className="w-4 h-4" /> Back
                </button>
                <button
                  onClick={() => setStep(3)}
                  disabled={!date || !timeSlot}
                  className="group inline-flex items-center gap-2 bg-[#d4af37] text-black px-8 py-3.5 overline text-xs disabled:opacity-30 disabled:cursor-not-allowed hover:bg-[#c5a028] transition-colors"
                  data-testid="continue-to-details"
                >
                  Continue <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            </motion.div>
          )}

          {/* STEP 3: Details */}
          {step === 3 && (
            <motion.div key="s3" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.4 }} data-testid="step-details">
              <h1 className="font-display text-3xl lg:text-5xl text-white mb-2">Your details</h1>
              {!user && (
                <p className="text-white/50 font-body mb-10">
                  Booking as a guest. <Link to="/login" className="text-[#d4af37] underline underline-offset-4">Sign in</Link> to track your appointments.
                </p>
              )}
              {user && <p className="text-white/50 font-body mb-10 flex items-center gap-2"><User className="w-4 h-4 text-[#d4af37]" /> Signed in as {user.name}</p>}

              <div className="grid sm:grid-cols-2 gap-6 mb-6">
                <Field label="Full Name *" value={details.customer_name} onChange={(v) => setDetails({ ...details, customer_name: v })} testid="input-name" />
                <Field label="Phone *" value={details.customer_phone} onChange={(v) => setDetails({ ...details, customer_phone: v })} testid="input-phone" />
              </div>
              <div className="mb-6">
                <Field label="Email (optional)" type="email" value={details.customer_email} onChange={(v) => setDetails({ ...details, customer_email: v })} testid="input-email" />
              </div>
              <div className="mb-10">
                <Field label="Notes (optional)" value={details.notes} onChange={(v) => setDetails({ ...details, notes: v })} testid="input-notes" />
              </div>

              <div className="mb-10">
                <label className="overline text-[0.6rem] text-white/50 block mb-2">Gift card code (optional)</label>
                <input
                  value={giftCode}
                  onChange={(e) => setGiftCode(e.target.value.toUpperCase())}
                  placeholder="TBC-XXXX-XXXX"
                  className="w-full bg-transparent border-b border-white/20 py-3 text-white font-body tracking-widest focus:border-[#d4af37] focus:outline-none transition-colors"
                  data-testid="input-gift-code"
                />
                {giftCode.trim() && <p className="text-[#d4af37]/80 text-xs font-body mt-2">Your gift card will be applied to this booking. Any balance stays on the card.</p>}
              </div>

              {payEnabled && !giftCode.trim() && (
                <div className="mb-10">
                  <p className="overline text-xs text-[#d4af37] mb-4">Payment</p>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <button
                      type="button"
                      onClick={() => setPayChoice("shop")}
                      className={`text-left border p-5 transition-colors duration-300 ${payChoice === "shop" ? "border-[#d4af37] bg-[#d4af37]/5" : "border-white/15 hover:border-white/30"}`}
                      data-testid="pay-at-shop"
                    >
                      <div className="flex items-center gap-3 mb-1">
                        <Store className={`w-5 h-5 ${payChoice === "shop" ? "text-[#d4af37]" : "text-white/50"}`} strokeWidth={1.5} />
                        <span className="font-body text-white">Pay at the shop</span>
                      </div>
                      <p className="text-white/40 text-xs font-body pl-8">Pay in person after your cut.</p>
                    </button>
                    <button
                      type="button"
                      onClick={() => setPayChoice("online")}
                      className={`text-left border p-5 transition-colors duration-300 ${payChoice === "online" ? "border-[#d4af37] bg-[#d4af37]/5" : "border-white/15 hover:border-white/30"}`}
                      data-testid="pay-online"
                    >
                      <div className="flex items-center gap-3 mb-1">
                        <CreditCard className={`w-5 h-5 ${payChoice === "online" ? "text-[#d4af37]" : "text-white/50"}`} strokeWidth={1.5} />
                        <span className="font-body text-white">Pay now by card</span>
                      </div>
                      <p className="text-white/40 text-xs font-body pl-8">Secure checkout via Stripe.</p>
                    </button>
                  </div>
                </div>
              )}

              <div className="border border-white/10 p-6 bg-[#0a0a0a] mb-6">
                <p className="overline text-xs text-[#d4af37] mb-4">Summary</p>
                {location && (
                  <SummaryRow icon={MapPin} label={locations.find((l) => l.id === location)?.name} value={locations.find((l) => l.id === location)?.short} />
                )}
                <SummaryRow icon={Scissors} label={selected?.name} value={`$${selected?.price}`} />
                <SummaryRow icon={CalIcon} label={date && date.toDateString()} value={timeSlot} />
              </div>

              <label className="flex items-start gap-3 mb-10 cursor-pointer select-none" data-testid="policy-agree-label">
                <input
                  type="checkbox"
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                  className="accent-[#d4af37] w-4 h-4 mt-0.5 shrink-0"
                  data-testid="policy-agree-checkbox"
                />
                <span className="font-body text-sm text-white/60 leading-relaxed">
                  I agree to the <Link to="/policies" target="_blank" className="text-[#d4af37] underline underline-offset-2" data-testid="policy-link">cancellation & no-show policy</Link>. Free changes up to {CANCELLATION_WINDOW_HOURS}h before; late cancellations/no-shows may incur a fee or deposit.
                </span>
              </label>

              <div className="flex justify-between">
                <button onClick={() => setStep(2)} className="inline-flex items-center gap-2 overline text-xs text-white/50 hover:text-white transition-colors" data-testid="back-to-datetime">
                  <ArrowLeft className="w-4 h-4" /> Back
                </button>
                <button
                  onClick={submit}
                  disabled={submitting || !agreed}
                  className="group inline-flex items-center gap-2 bg-[#d4af37] text-black px-8 py-3.5 overline text-xs disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#c5a028] transition-colors"
                  data-testid="confirm-booking-btn"
                >
                  {submitting ? "Processing…" : (payEnabled && payChoice === "online" && !giftCode.trim() ? "Continue to Payment" : "Confirm Booking")} <Check className="w-4 h-4" />
                </button>
              </div>
            </motion.div>
          )}

          {/* STEP 4: Confirmation */}
          {step === 4 && confirmation && (
            <motion.div key="s4" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }} className="text-center py-10" data-testid="step-confirmed">
              <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.2, type: "spring" }} className="w-20 h-20 border-2 border-[#d4af37] rounded-full flex items-center justify-center mx-auto mb-8">
                <Check className="w-9 h-9 text-[#d4af37]" strokeWidth={2} />
              </motion.div>
              <h1 className="font-display text-4xl lg:text-6xl text-white mb-4">You&apos;re <span className="italic text-[#d4af37]">booked.</span></h1>
              <p className="text-white/50 font-body mb-10 max-w-md mx-auto">See you in the chair, {confirmation.customer_name.split(" ")[0]}. A confirmation has been logged.</p>

              <div className="border border-white/10 p-8 bg-[#0a0a0a] max-w-md mx-auto text-left mb-10">
                {confirmation.location_name && (
                  <SummaryRow icon={MapPin} label={confirmation.location_name} value={confirmation.location_address || ""} />
                )}
                <SummaryRow icon={Scissors} label={confirmation.service_name} value={`$${confirmation.service_price}`} />
                <SummaryRow icon={CalIcon} label={new Date(confirmation.date + "T00:00:00").toDateString()} value={confirmation.time_slot} />
                <SummaryRow icon={User} label={confirmation.customer_name} value={confirmation.customer_phone} />
              </div>

              <div className="max-w-md mx-auto mb-10">
                <ShareButtons text={`Just booked with Reek at The Barbers Chair 💈 Book your cut online:`} label="Tell your people" />
              </div>

              <div className="flex items-center justify-center gap-4">
                <button onClick={() => navigate("/")} className="border border-white/20 px-8 py-3.5 overline text-xs text-white hover:border-[#d4af37] hover:text-[#d4af37] transition-colors" data-testid="back-home-btn">
                  Back Home
                </button>
                {user && (
                  <button onClick={() => navigate("/account")} className="bg-[#d4af37] text-black px-8 py-3.5 overline text-xs hover:bg-[#c5a028] transition-colors" data-testid="view-bookings-btn">
                    My Bookings
                  </button>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="overline text-[0.6rem] text-white/50 block mb-2">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-transparent border-b border-white/20 py-3 text-white font-body focus:border-[#d4af37] focus:outline-none transition-colors"
        data-testid={testid}
      />
    </div>
  );
}

function SummaryRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center justify-between py-3 border-b border-white/5 last:border-0">
      <div className="flex items-center gap-3 text-white/70 font-body text-sm">
        <Icon className="w-4 h-4 text-[#d4af37]" strokeWidth={1.5} /> {label}
      </div>
      <span className="text-white font-body text-sm">{value}</span>
    </div>
  );
}
